import { FC, useEffect, useMemo, useState } from "react";

import classNames from "classnames";

export interface CsvFilePreviewProps {
  csvUrl: string;
  delimiter?: string;
  className?: string;
  maxRows?: number; // guard for very large CSVs
  showJson?: boolean; // if true, also render the JSON below the table
}

/**
 * Lightweight CSV parser that supports quoted fields and doubled quotes.
 */
function parseCsvToRows(text: string, delimiter = ","): string[][] {
  const rows: string[][] = [];
  if (!text) return rows;

  let cur = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (ch === '"') {
      // Handle escaped quotes ""
      if (inQuotes && text[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes) {
      if (ch === delimiter) {
        row.push(cur);
        cur = "";
        continue;
      }
      if (ch === "\r") {
        continue;
      }
      if (ch === "\n") {
        row.push(cur);
        rows.push(row);
        row = [];
        cur = "";
        continue;
      }
    }

    cur += ch;
  }

  // push last cell/row
  if (cur !== "" || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }

  return rows;
}

const CsvFilePreview: FC<CsvFilePreviewProps> = ({
  csvUrl,
  delimiter = ",",
  className,
  maxRows = 200,
  showJson = false,
}) => {
  const [csvText, setCsvText] = useState("");

  useEffect(() => {
    const fetchCsv = async () => {
      try {
        const response = await fetch(csvUrl);
        const text = await response.text();
        setCsvText(text);
      } catch (error) {
        console.error("Error fetching CSV:", error);
        setCsvText("");
      }
    };

    if (csvUrl) {
      fetchCsv();
    }
  }, [csvUrl]);

  const { headers, records } = useMemo(() => {
    const rows = parseCsvToRows(csvText, delimiter);
    if (rows.length === 0) return { headers: [], records: [] };

    const rawHeaders = rows[0].map((h, i) => (h && h.trim() ? h.trim() : `col_${i}`));
    const recs = rows.slice(1).map((r) => {
      const obj: Record<string, string> = {};
      for (let i = 0; i < rawHeaders.length; i++) {
        obj[rawHeaders[i]] = r[i] ?? "";
      }
      // include any extra columns without headers
      for (let j = rawHeaders.length; j < r.length; j++) {
        obj[`col_${j}`] = r[j];
      }
      return obj;
    });
    return { headers: rawHeaders, records: recs };
  }, [csvText, delimiter]);

  if (!headers.length) {
    return <div className={className ?? ""}>No CSV data</div>;
  }

  const limitedRecords = records.slice(0, maxRows);

  return (
    <div className={classNames(className ?? "", "h-full overflow-y-scroll")}>
      <div>
        <table style={{ borderCollapse: "collapse", width: "100%", height: "100%" }}>
          <thead>
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  style={{ textAlign: "left", border: "1px solid #ddd", padding: "6px", background: "#f7f7f7" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {limitedRecords.map((rec, i) => (
              <tr key={i}>
                {headers.map((h) => (
                  <td key={h} style={{ border: "1px solid #eee", padding: "6px" }}>
                    {rec[h]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {records.length > maxRows && (
        <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
          Showing {maxRows} of {records.length} rows
        </div>
      )}

      {showJson && (
        <pre style={{ marginTop: 12, background: "#fafafa", padding: 8, overflowX: "auto" }}>
          {JSON.stringify(records, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default CsvFilePreview;
